import { Injectable, Injector, OnDestroy, Optional, Provider, signal, computed, SkipSelf } from "@angular/core"
import { BehaviorSubject, delay, shareReplay, Subscription, tap } from "rxjs"
import { Customer, CustomerBackgroundProvider, CustomerDonation, CustomerMarketingDetails, OnTimePaymentIncentive, PotentialGiftWithContacts, Ppm365SubscribeHistoryResponse, Ptrip, TripDetail } from "src/app/api/commonInterfaces"
import { ContactsService } from "src/app/api/contacts.service"
import { CustomerDonationService } from "src/app/api/customer-donations.service"
import { CustomerService } from "src/app/api/customer.service"
import { MarketingTouchLinkService } from "src/app/api/marketing-touch-link.service"
import { ParticipantService } from "src/app/api/participant.service"
import { PotentialGiftsService } from "src/app/api/potential-gifts.service"
import { PotentialTripService } from "src/app/api/potential-trip.service"
import { ProfilesPartnerService } from "src/app/api/profiles-partner.service"
import { TripDetailService } from "src/app/api/trip-detail.service"
import { TripFinanceService } from "src/app/api/trip-finance.service"
import { TripService } from "src/app/api/trip.service"
import { Contact } from "src/app/api/types/contacts"
import { CustomerCredit, RecurringDonation, WalletTransaction } from "src/app/api/types/finance"
import { ProfilesPartner, ProfilesCommunity } from "src/app/api/types/partnerships"
import { MTCustomerLink } from "src/app/api/types/recruiting"
import { WalletService } from "src/app/api/wallet.service"
import { ProfilesCommunityService } from "src/app/api/profiles-community.service"

interface TripLoadingObject {
    potentialTrips: boolean,
    contacts: boolean,
    trips: boolean,
    credits: boolean,
    walletTransactions: boolean,
    marketingTouches: boolean,
    marketingDetails: boolean,
    onTimeIncentives: boolean,
    backgroundCheckProviders: boolean,

}

interface FundraisingLoadingObject {
    contacts: boolean,
    children: boolean,
    donations: boolean,
    potentialGifts: boolean,

}

interface LoadingObject {
    potentialTrips: boolean,
    contacts: boolean,
    trips: boolean,
    credits: boolean,
    walletTransactions: boolean,
    marketingTouches: boolean,
    marketingDetails: boolean,
    onTimeIncentives: boolean,
    backgroundCheckProviders: boolean,
    children: boolean,
    donations: boolean,
    potentialGifts: boolean,
    partnersDetails: boolean,
    communityDetails: boolean,

}

/**
 * Handles the data necessary for the contact card to function
 * 
 * Use the loadCustomer() function to trigger loading a customer.  The customer will be 
 * emitted through the customer$ observable, so subscribe to it first.  If no customer is loaded,
 * an empty customer will be emitted immediately and the requested customer once the http call is complete.
 * 
 * 
 * If you just need some supporting data in a component further down the tree, subscribe to the
 * appropriate observable.  They will emit the current value immediately, and a new value if
 * data should be updated.
 */
@Injectable()
export class ContactCardStateService implements OnDestroy {

    parentInit = false


    private _customer: Customer = this.customerService.emptyCustomer()

    get customer(): Customer {
        return this._customer
    }

    private _customerSubject = new BehaviorSubject<Customer>(this.customerService.emptyCustomer())
    customer$ = this._customerSubject.asObservable()

    private _tripLoadComplete = false
    private _tripLoadCompleteSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
    tripLoadComplete$ = this._tripLoadCompleteSubject.asObservable()

    private _fundraisingLoadComplete = false
    private _fundraisingLoadCompleteSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
    fundraisingLoadComplete$ = this._fundraisingLoadCompleteSubject.asObservable()

    private _tripDetails: TripDetail[] = []

    get tripDetails(): TripDetail[] {
        return this._tripDetails
    }

    private _fetchingTripDetails = false
    private _tripDetailSubject = new BehaviorSubject<TripDetail[]>([])
    tripDetails$ = this._tripDetailSubject.asObservable().pipe(
        tap(() => {
            if (this.loadingIndicators().trips === true && this._fetchingTripDetails === false && this.customer.id !== 0) {
                this.loadTripDetails()
            }
        })
    )

    private _mtLinks: MTCustomerLink[] = []

    get mtLinks(): MTCustomerLink[] {
        return this._mtLinks
    }

    private _mtLinkSubject = new BehaviorSubject<MTCustomerLink[]>([])
    mtLinks$ = this._mtLinkSubject.asObservable()

    private _walletTransactions: WalletTransaction[] = []

    get walletTransactions(): WalletTransaction[] {
        return this._walletTransactions
    }

    private _walletTransactionSubject = new BehaviorSubject<WalletTransaction[]>([])
    walletTransactions$ = this._walletTransactionSubject.asObservable()

    private _credits: CustomerCredit[] = []

    get credits(): CustomerCredit[] {
        return this._credits
    }

    private _creditSubject = new BehaviorSubject<CustomerCredit[]>([])
    credits$ = this._creditSubject.asObservable()


    private _pTrips: Ptrip[] = []

    get pTrips(): Ptrip[] {
        return this._pTrips
    }

    private _pTripSubject: BehaviorSubject<Ptrip[]> = new BehaviorSubject<Ptrip[]>([])
    pTrips$ = this._pTripSubject.asObservable()

    private _contacts: Contact[] = []

    get contacts(): Contact[] {
        return this._contacts
    }

    private _partnersDetails: ProfilesPartner[] = []

    get partnersDetails(): ProfilesPartner[] {
        return this._partnersDetails
    }

    private _partnersDetailsSubject: BehaviorSubject<ProfilesPartner[]> = new BehaviorSubject<ProfilesPartner[]>([])
    partnersDetails$ = this._partnersDetailsSubject.asObservable()

    private _communityDetails: ProfilesCommunity[] = []
    get communityDetails(): ProfilesCommunity[] {
        return this._communityDetails
    }
    private _communityDetailsSubject: BehaviorSubject<ProfilesCommunity[]> = new BehaviorSubject<ProfilesCommunity[]>([])
    communityDetails$ = this._communityDetailsSubject.asObservable()
    private _fetchingCommunityDetails = false

    private _contactSubject: BehaviorSubject<Contact[]> = new BehaviorSubject<Contact[]>([])
    contacts$ = this._contactSubject.asObservable()

    private _backgroundProviders: CustomerBackgroundProvider[] = []

    get backgroundProviders(): CustomerBackgroundProvider[] {
        return this._backgroundProviders
    }

    private _backgroundProviderSubject: BehaviorSubject<CustomerBackgroundProvider[]> = new BehaviorSubject<CustomerBackgroundProvider[]>([])
    backgroundProviders$ = this._backgroundProviderSubject.asObservable()

    private _marketingDetails: CustomerMarketingDetails | null = null

    get marketingDetails(): CustomerMarketingDetails | null {
        return this._marketingDetails
    }

    private _marketingDetailsSubject: BehaviorSubject<CustomerMarketingDetails | null> = new BehaviorSubject<CustomerMarketingDetails | null>(null)
    marketingDetails$ = this._marketingDetailsSubject.asObservable()

    private _onTimeIncentives: OnTimePaymentIncentive[] = []

    get onTimePaymentIncentives(): OnTimePaymentIncentive[] {
        return this._onTimeIncentives
    }

    private _onTimePaymentIncentivesSubject: BehaviorSubject<OnTimePaymentIncentive[]> = new BehaviorSubject<OnTimePaymentIncentive[]>([])
    onTimePaymentIncentives$ = this._onTimePaymentIncentivesSubject.asObservable()

    private _children: Customer[] = []
    get children(): Customer[] {
        return this._children
    }
    private _fetchingChildren = false
    private _childrenSubject: BehaviorSubject<Customer[]> = new BehaviorSubject<Customer[]>([])
    children$ = this._childrenSubject.asObservable().pipe(
        tap(() => {
            if (this.fundraisingLoading().children === true && this._fetchingChildren === false && this.customer.id !== 0) {
                this.loadChildren()
            }
        }))

    private _donations: CustomerDonation[] = []
    get donations(): CustomerDonation[] {
        return this._donations
    }
    private _fetchingDonations = false
    private _donationSubject: BehaviorSubject<CustomerDonation[]> = new BehaviorSubject<CustomerDonation[]>([])
    donations$ = this._donationSubject.asObservable().pipe(
        tap(() => {
            if (this.fundraisingLoading().donations === true && this._fetchingDonations === false && this.customer.id !== 0) {
                this.loadDonations()
            }
        }))


    private _potentialGifts: PotentialGiftWithContacts[] = []
    get potentialGifts(): PotentialGiftWithContacts[] {
        return this._potentialGifts
    }
    private _fetchingPotentialGifts = false
    private _potentialGiftSubject: BehaviorSubject<PotentialGiftWithContacts[]> = new BehaviorSubject<PotentialGiftWithContacts[]>([])
    potentialGifts$ = this._potentialGiftSubject.asObservable().pipe(
        tap(() => {
            if (this.fundraisingLoading().potentialGifts === true && this._fetchingPotentialGifts === false && this.customer.id !== 0) {
                this.loadPotentialGifts()
            }
        }))


    subscriptions: Subscription[] = []

    loadingIndicators = signal<LoadingObject>({
        potentialTrips: true,
        contacts: true,
        trips: true,
        credits: true,
        walletTransactions: true,
        marketingTouches: true,
        marketingDetails: true,
        onTimeIncentives: true,
        backgroundCheckProviders: true,
        children: true,
        donations: true,
        potentialGifts: true,
        partnersDetails: true,
        communityDetails: true,


    })

    tripLoading = computed<TripLoadingObject>(() => {
        const indicators = this.loadingIndicators()

        return {
            potentialTrips: indicators.potentialTrips,
            contacts: indicators.contacts,
            trips: indicators.trips,
            credits: indicators.credits,
            walletTransactions: indicators.walletTransactions,
            marketingTouches: indicators.marketingTouches,
            marketingDetails: indicators.marketingDetails,
            onTimeIncentives: indicators.onTimeIncentives,
            backgroundCheckProviders: indicators.backgroundCheckProviders
        }
    })

    fundraisingLoading = computed<FundraisingLoadingObject>(() => {
        const indicators = this.loadingIndicators()

        return {
            contacts: indicators.contacts,
            children: indicators.children,
            donations: indicators.donations,
            potentialGifts: indicators.potentialGifts,

        }
    })




    constructor(
        private customerService: CustomerService,
        private contactService: ContactsService,
        private potentialTripService: PotentialTripService,
        private walletService: WalletService,
        private mtLinkService: MarketingTouchLinkService,
        private tripDetailService: TripDetailService,
        private tripService: TripService,
        private tripFinanceService: TripFinanceService,
        private customerDonationService: CustomerDonationService,
        private potentialGiftsService: PotentialGiftsService,
        private participantService: ParticipantService,
        private profilesPartnerService: ProfilesPartnerService,
        private profilesCommunityService: ProfilesCommunityService
    ) {



        this.subscriptions.push(
            this.customerService.customerUpdated.subscribe(customerId => {
                if (this._customer && this._customer.id === customerId) {
                    this.reloadCustomer()
                }
            })
        )

        this.subscriptions.push(
            this.contactService.contactUpdated.subscribe(args => {
                this.loadContacts()
            })
        )

        this.subscriptions.push(
            this.potentialTripService.potentialTripUpdated$.subscribe(args => {
                this.loadPotentialTrips()
            })
        )

        this.subscriptions.push(
            this.tripDetailService.tripUpdated.subscribe(args => {
                const existingIndex = this.tripDetails.findIndex(t => t.id === args.trip.id)
                if (existingIndex > -1) {
                    this.tripDetails[existingIndex] = args.tripDetail
                    this._tripDetails = [...this.tripDetails] //trigger change detection
                    this._tripDetailSubject.next(this._tripDetails)

                }
            })
        )

        this.subscriptions.push(
            this.tripService.tripAssigned$.subscribe(args => {
                if (this.customer.id === args.customerId) {
                    this.loadTripDetails()
                    this.loadOnTimePaymentIncentives() //we may have used an incentive on the trip
                }
            })
        )

        this.subscriptions.push(
            this.walletService.walletUpdated$.subscribe(args => {
                if (this.credits.some(c => c.id === args.creditId) || args.operation === "added") {
                    this.loadCredits()
                    this.loadWalletTransactions()
                }
            })
        )

        this.subscriptions.push(
            this.customerService.marketingDetailsUpdated$.subscribe(args => {
                if (this.marketingDetails && args.marketingDetails.id === this.marketingDetails.id) {
                    this._marketingDetails = args.marketingDetails
                    this._marketingDetailsSubject.next(this._marketingDetails)
                }
            })
        )

        this.subscriptions.push(
            this.tripFinanceService.paymentsUpdated$.subscribe(args => {
                this.loadOnTimePaymentIncentives()
                this.loadWalletTransactions()
                this.loadCredits()
            })
        )

        this.subscriptions.push(
            this.potentialGiftsService.potentialGiftUpdated$.subscribe(args => {
                this.loadPotentialGifts()
            })

        )

        this.subscriptions.push(this.participantService.participantUpdated.subscribe(args => {

            if (this.tripDetails.some(t => t.id === args.tripId) && (args.operation === "remove" )) {
                this.loadTripDetails()
            }

        }))


        this.subscriptions.push(
            this.profilesPartnerService.profilesPartner$.subscribe(args => {
                this.loadPartnersDetails()
            })
        )

        this.subscriptions.push(
            this.profilesCommunityService.profilesCommunity$.subscribe(() => {
                this.loadCommunityDetails()
            })
        )
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe())
    }

    resetLoading() {
        this._tripLoadComplete = false
        this._fundraisingLoadComplete = false

        this.loadingIndicators.set({
            potentialTrips: true,
            contacts: true,
            trips: true,
            credits: true,
            walletTransactions: true,
            marketingTouches: true,
            marketingDetails: true,
            onTimeIncentives: true,
            backgroundCheckProviders: true,
            children: true,
            donations: true,
            potentialGifts: true,
    partnersDetails: true,  
            communityDetails: true,
        })

    }

    checkTripLoadComplete() {
        const tripLoading = this.tripLoading()
        if (
            this._tripLoadComplete === false &&
            tripLoading.potentialTrips === false &&
            tripLoading.contacts === false &&
            tripLoading.trips === false &&
            tripLoading.credits === false &&
            tripLoading.walletTransactions === false &&
            tripLoading.marketingTouches === false &&
            tripLoading.contacts === false &&
            tripLoading.backgroundCheckProviders === false

        ) {
            this._tripLoadCompleteSubject.next(true)
            this._tripLoadComplete = true
        }
    }

    checkFundraisingLoadComplete() {
        const fundraisingLoading = this.fundraisingLoading()
        if (
            this._fundraisingLoadComplete === false &&
            fundraisingLoading.contacts === false &&
            fundraisingLoading.children === false &&
            fundraisingLoading.donations === false &&
            fundraisingLoading.potentialGifts === false

        ) {
            this._fundraisingLoadCompleteSubject.next(true)
            this._fundraisingLoadComplete = true
        }
    }

    /**
     * Loads the customer for the provided id and begins the loading process for supporting data.  Subscribe to the provided observables
     * to receive the data when loading is complete.  The observables will emit again if the data is changed in this app instance.
     * @param customerId The customer to load
     */
    loadCustomer(customerId: number, loadTypes?: { loadTrip?: boolean, loadFundraising?: boolean }) {
        const { loadTrip, loadFundraising } = loadTypes ?? { loadTrip: true, loadFundraising: false }

        const previousLoadIndicators = { ...this.loadingIndicators() }
        this.resetLoading()
        if (this.customer.id !== 0 && this.customer.id != customerId) {
            this.resetAllData()
        }
        this.customerService.getCustomer(customerId).subscribe(
            customer => {
                this._customer = customer


                if (loadTrip || previousLoadIndicators.potentialTrips === false) this.loadPotentialTrips()
                if (loadTrip || previousLoadIndicators.credits === false) this.loadCredits()
                if (loadTrip || previousLoadIndicators.marketingTouches === false) this.loadMarketingTouches()
                if (loadTrip || previousLoadIndicators.walletTransactions === false) this.loadWalletTransactions()
                if (loadTrip || previousLoadIndicators.trips === false) this.loadTripDetails()
                if (loadTrip || previousLoadIndicators.marketingDetails === false) this.loadMarketingDetails()
                if (loadTrip || previousLoadIndicators.onTimeIncentives === false) this.loadOnTimePaymentIncentives()
                if (loadTrip || previousLoadIndicators.backgroundCheckProviders === false) this.loadBackgroundProviders()

                if (loadFundraising || previousLoadIndicators.children === false) this.loadChildren()
                if (loadFundraising || previousLoadIndicators.donations === false) this.loadDonations()
                if (loadFundraising || previousLoadIndicators.potentialGifts === false) this.loadPotentialGifts()
                if (loadTrip || loadFundraising || previousLoadIndicators.contacts === false) this.loadContacts()
                if (loadTrip || previousLoadIndicators.partnersDetails === false) this.loadPartnersDetails()
                if (loadTrip || previousLoadIndicators.communityDetails === false) this.loadCommunityDetails()

                this._customerSubject.next(this._customer)

            }
        )

    }

    resetAllData() {
        this._customer = this.customerService.emptyCustomer()
        this._customerSubject.next(this._customer)

        this._backgroundProviders = []
        this._backgroundProviderSubject.next([])

        this._children = []
        this._childrenSubject.next([])

        this._contacts = []
        this._contactSubject.next([])

        this._credits = []
        this._creditSubject.next([])

        this._donations = []
        this._donationSubject.next([])

        this._marketingDetails = null
        this._marketingDetailsSubject.next(null)

        this._mtLinks = []
        this._mtLinkSubject.next([])

        this._onTimeIncentives = []
        this._onTimePaymentIncentivesSubject.next([])

        this._pTrips = []
        this._pTripSubject.next([])

        this._potentialGifts = []
        this._potentialGiftSubject.next([])

        this._tripDetails = []
        this._tripDetailSubject.next([])

        this._walletTransactions = []
        this._walletTransactionSubject.next([])

        this._partnersDetails = []
        this._partnersDetailsSubject.next([])

        this._communityDetails = []
        this._communityDetailsSubject.next([])

    }

    reloadCustomer() {
        this.customerService.getCustomer(this.customer.id).subscribe(
            customer => {
                this._customer = customer
                this._customerSubject.next(this._customer)
            }
        )
    }

    loadBackgroundProviders() {
        this.customerService.getBackgroundCheckProviders(this.customer.id).subscribe(
            backgroundProviders => {
                this._backgroundProviders = backgroundProviders
                this.loadingIndicators.update(i => ({ ...i, backgroundCheckProviders: false }))
                this._backgroundProviderSubject.next(this._backgroundProviders)
            }
        )
    }

    loadContacts() {
        this.contactService.getContactsForCustomer(this.customer.id).subscribe(
            contacts => {
                this._contacts = contacts
                this.loadingIndicators.update(i => ({ ...i, contacts: false }))
                this._contactSubject.next(this._contacts)
                this.checkTripLoadComplete()
                this.checkFundraisingLoadComplete()
            })
    }

    loadPotentialTrips() {

        this.potentialTripService.getForCustomer(this.customer.id).subscribe(pTrips => {
            this._pTrips = pTrips
            this.loadingIndicators.update(i => ({ ...i, potentialTrips: false }))
            this._pTripSubject.next(pTrips)
            this.checkTripLoadComplete()

        })
    }

    loadCredits() {
        this.walletService.getCreditsForCustomer(this.customer.id).subscribe(credits => {
            this._credits = credits
            this.loadingIndicators.update(i => ({ ...i, credits: false }))

            this._creditSubject.next(credits)
            this.checkTripLoadComplete()
        })
    }

    loadWalletTransactions() {
        this.walletService.getTransactionsForCustomer(this.customer.id).subscribe(txns => {
            this._walletTransactions = txns
            this.loadingIndicators.update(i => ({ ...i, walletTransactions: false }))
            this._walletTransactionSubject.next(txns)
            this.checkTripLoadComplete()
        })
    }

    loadMarketingTouches() {
        this.mtLinkService.getForCustomer(this.customer.id).subscribe(links => {
            this._mtLinks = links
            this.loadingIndicators.update(i => ({ ...i, marketingTouches: false }))

            this._mtLinkSubject.next(links)
            this.checkTripLoadComplete()
        })
    }

    loadTripDetails() {
        this.tripDetailService.getTripsForCustomer(this.customer.id).subscribe(tripDetails => {
            this._tripDetails = tripDetails
            this.loadingIndicators.update(i => ({ ...i, trips: false }))
            this._tripDetailSubject.next(tripDetails)
            this.checkTripLoadComplete()
        })
    }

    loadMarketingDetails() {
        this.customerService.getCustomerMarketingDetails(this.customer.id).subscribe(details => {
            this._marketingDetails = details
            this.loadingIndicators.update(i => ({ ...i, marketingDetails: false }))
            this._marketingDetailsSubject.next(details)
            this.checkTripLoadComplete()
        })
    }

    loadOnTimePaymentIncentives() {
        this.customerService.getOnTimeIncentiveStatus(this.customer.id).subscribe(incentives => {
            this._onTimeIncentives = incentives
            this.loadingIndicators.update(i => ({ ...i, onTimeIncentives: false }))
            this._onTimePaymentIncentivesSubject.next(incentives)
            this.checkTripLoadComplete()

        })
    }

    loadChildren() {
        if (this.customer.id !== 0) {
            this._fetchingChildren = true
            this.customerService.getCustomerChildren(this.customer.id).subscribe(children => {
                this._children = children
                this.loadingIndicators.update(i => ({ ...i, children: false }))
                this._childrenSubject.next(children)
                this.checkFundraisingLoadComplete()
            })
        }
    }

    loadDonations() {
        if (this.customer.id !== 0) {
            this._fetchingDonations = true
            this.customerDonationService.getCustomerDonations(this.customer.id, { includeChildren: true }).subscribe(donations => {
                this._donations = donations
                this.loadingIndicators.update(i => ({ ...i, donations: false }))
                this._donationSubject.next(donations)
                this.checkFundraisingLoadComplete()
            })
        }
    }

    loadPotentialGifts() {
        if (this.customer.id !== 0) {
            this._fetchingPotentialGifts = true
            this.potentialGiftsService.getForCustomer(this.customer.id).subscribe(potentialGifts => {
                this._potentialGifts = potentialGifts
                this.loadingIndicators.update(i => ({ ...i, potentialGifts: false }))
                this._potentialGiftSubject.next(potentialGifts)
                this._fetchingPotentialGifts = false
                this.checkFundraisingLoadComplete()
            })
        }
    }

    loadPartnersDetails() {
        this.profilesPartnerService.getAllProfilesPartner().subscribe(partners => {
            this._partnersDetails = partners
            this.loadingIndicators.update(i => ({ ...i, partnersDetails: false }))
            this._partnersDetailsSubject.next(partners)
            this.checkTripLoadComplete()
        })
    }

    loadCommunityDetails() {
        this.profilesCommunityService.getAllProfilesCommunity().subscribe(communities => {
            this._communityDetails = communities
            this.loadingIndicators.update(i => ({ ...i, communityDetails: false }))
            this._communityDetailsSubject.next(communities)
            this.checkTripLoadComplete()
        })
    }


    /**
     * Provider factory that checks the parent first, then creates a new instance if none is found
     * @param parentInjector 
     * @param existingService 
     * @returns 
     */
    static providerFactory = (parentInjector: Injector, existingService?: ContactCardStateService) => {
        if (existingService) {
            return existingService
        }
        else {
            const injector = Injector.create({
                providers: [{ provide: ContactCardStateService }],
                parent: parentInjector
            })
            return injector.get(ContactCardStateService)
        }
    }

    static useParentIfAvailableProvider: Provider = {
        provide: ContactCardStateService,
        useFactory: ContactCardStateService.providerFactory,
        deps: [Injector, [new Optional(), new SkipSelf(), ContactCardStateService]]
    }

}